import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Edit, Shield, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { DateRange } from "react-day-picker";
import { Button } from "../components/ui/button";
import { Icons } from "../components/ui/icons";
import { Input } from "../components/ui/input";
import { Modal } from "../components/ui/modal";
import { CalendarDatePicker } from "../components/ui/calendar-date-picker";
import StatisticsCard from "../components/statistics-card";
import TabButton from "../components/tab-button";
import TabBar from "../components/tab-bar";
import PageShell, { StatItem } from "../components/page-shell";
import FilterField from "../components/mobile/filter-field";
import { ActionSheetItem } from "../components/mobile/action-sheet";
import { MobilePrimaryAction } from "../components/mobile/types";
import { DesktopOnly } from "../components/responsive";
import { useIsMobile } from "../hooks/use-is-mobile";
import SelectInput from "../components/select-input";
import SearchableSelectInput from "../components/searchable-select-input";
import SearchInput from "../components/search-input";
import DataTable, { TableRowData } from "../components/data-table";
import { RefreshToolbarButton } from "../components/refresh-toolbar-button";
import PpeTopProvisionedChart from "../components/ppe-top-provisioned-chart";
import DateRangePicker from "../components/date-range-picker";
import { Textarea } from "../components/ui/textarea";
import PpeUserProvisionsDrawer from "../components/ppe-user-provisions-drawer";
import PpeRecordDetailsDrawer from "../components/ppe-record-details-drawer";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  ppeProvisionsTableColumns,
  ppePurchasesTableColumns,
  ppeStockTableColumns,
  ppeUsersTableColumns,
} from "../lib/table-columns";
import {
  Ppe,
  PpeCondition,
  PpeProvision,
  PpePurchase,
  PpeUser,
  Warehouse,
  createPpe,
  createPpeUser,
  getPpeList,
  getPpeProvisions,
  getPpePurchases,
  getPpeUserProvisions,
  getPpeStatistics,
  getPpeUsers,
  getWarehouses,
  providePpe,
  purchasePpe,
  updatePpe,
  updatePpeUser,
  PpeStatistics,
} from "../lib/api";
import { getBackendErrorMessage } from "../lib/utils";
import { useUser } from "../hooks/use-user";
import * as XLSX from "xlsx";

type Tab = "Stock" | "Users" | "Provisions" | "Purchases";

/** How PPE stock + stats are scoped to the warehouse API (mirrors Inventory). */
type PpeDataQuery =
  | { status: "pending" }
  | { status: "empty" }
  | { status: "scoped"; warehouseId: string }
  | { status: "all" };

function warehouseIdForPpeApi(q: PpeDataQuery): string | undefined {
  if (q.status === "scoped") return q.warehouseId;
  if (q.status === "all") return undefined;
  return undefined;
}

type ProvisionLine = {
  ppeId: string;
  quantity: string;
};

type PurchaseLine = {
  ppeId: string;
  quantity: string;
};

const PPE_CONDITIONS: PpeCondition[] = ["NEW", "GOOD", "FAIR", "WORN", "DAMAGED"];
const EMPTY_SELECT_VALUE = "__none__";

const parseDecimalQuantity = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const num = Number(trimmed);
  if (!Number.isFinite(num)) return null;
  return num;
};

const formatQuantity = (value: number) =>
  Number.isInteger(value) ? String(value) : value.toLocaleString(undefined, { maximumFractionDigits: 4 });

const formatStatQuantity = (value: number) =>
  Number.isInteger(value)
    ? value.toLocaleString()
    : value.toLocaleString(undefined, { maximumFractionDigits: 2 });

const formatPpeUserLabel = (user: PpeUser) => {
  const base = `${user.fullName} - ${user.jobTitle}`;
  const location = user.location?.trim();
  return location ? `${base} (${location})` : base;
};

const PpePage: React.FC = () => {
  const { user } = useUser();
  const isMobile = useIsMobile();
  const [tab, setTab] = useState<Tab>("Stock");
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const [stockPage, setStockPage] = useState(1);
  const [stockPageSize, setStockPageSize] = useState(10);

  const [usersPage, setUsersPage] = useState(1);
  const [usersPageSize, setUsersPageSize] = useState(10);

  const [provisionsPage, setProvisionsPage] = useState(1);
  const [provisionsPageSize, setProvisionsPageSize] = useState(10);

  const [purchasesPage, setPurchasesPage] = useState(1);
  const [purchasesPageSize, setPurchasesPageSize] = useState(10);

  const [selectedProvisionPpeUserId, setSelectedProvisionPpeUserId] = useState("all");
  const [selectedProvisionPpeId, setSelectedProvisionPpeId] = useState("all");
  const [selectedPurchasePpeId, setSelectedPurchasePpeId] = useState("all");
  const [selectedWarehouse, setSelectedWarehouse] = useState("");
  /** When true, user explicitly chose "All warehouses" in the filter. */
  const [viewsAllWarehouses, setViewsAllWarehouses] = useState(false);
  const [statsDateRange, setStatsDateRange] = useState<DateRange | undefined>(undefined);

  const [isPpeModalOpen, setIsPpeModalOpen] = useState(false);
  const [editingPpeId, setEditingPpeId] = useState<string | null>(null);
  const [ppeName, setPpeName] = useState("");
  const [ppeSize, setPpeSize] = useState("");
  const [ppeCondition, setPpeCondition] = useState<PpeCondition>("NEW");
  const [ppeQuantity, setPpeQuantity] = useState("");
  const [ppeUnit, setPpeUnit] = useState("");
  const [ppeWarehouseId, setPpeWarehouseId] = useState("");
  const [savingPpe, setSavingPpe] = useState(false);

  const [isPpeUserModalOpen, setIsPpeUserModalOpen] = useState(false);
  const [editingPpeUserId, setEditingPpeUserId] = useState<string | null>(null);
  const [ppeUserFullName, setPpeUserFullName] = useState("");
  const [ppeUserJobTitle, setPpeUserJobTitle] = useState("");
  const [ppeUserLocation, setPpeUserLocation] = useState("");
  const [savingPpeUser, setSavingPpeUser] = useState(false);

  const [isProvisionModalOpen, setIsProvisionModalOpen] = useState(false);
  const [provisionUserId, setProvisionUserId] = useState("");
  const [provisionDate, setProvisionDate] = useState<Date | null>(null);
  const [provisionItems, setProvisionItems] = useState<ProvisionLine[]>([
    { ppeId: "", quantity: "1" },
  ]);
  const [savingProvision, setSavingProvision] = useState(false);
  const [selectedPpeUserDetails, setSelectedPpeUserDetails] = useState<PpeUser | null>(null);
  const [selectedProvisionDetails, setSelectedProvisionDetails] =
    useState<PpeProvision | null>(null);
  const [selectedPurchaseDetails, setSelectedPurchaseDetails] =
    useState<PpePurchase | null>(null);

  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
  const [purchaseDate, setPurchaseDate] = useState<Date | null>(null);
  const [purchaseNotes, setPurchaseNotes] = useState("");
  const [purchaseItems, setPurchaseItems] = useState<PurchaseLine[]>([
    { ppeId: "", quantity: "1" },
  ]);
  const [savingPurchase, setSavingPurchase] = useState(false);
  const [downloadingProvisionsExcel, setDownloadingProvisionsExcel] = useState(false);

  const queryClient = useQueryClient();

  const resetPpeForm = () => {
    setEditingPpeId(null);
    setPpeName("");
    setPpeSize("");
    setPpeCondition("NEW");
    setPpeQuantity("");
    setPpeUnit("");
    setPpeWarehouseId("");
  };

  const resetPpeUserForm = () => {
    setEditingPpeUserId(null);
    setPpeUserFullName("");
    setPpeUserJobTitle("");
    setPpeUserLocation("");
  };

  const provisionsUserId =
    selectedProvisionPpeUserId !== "all" ? selectedProvisionPpeUserId : undefined;
  const provisionsPpeId =
    selectedProvisionPpeId !== "all" ? selectedProvisionPpeId : undefined;
  const purchasesPpeId =
    selectedPurchasePpeId !== "all" ? selectedPurchasePpeId : undefined;
  const useUserProvisionsEndpoint = Boolean(provisionsUserId && !provisionsPpeId);

  const resetProvisionForm = () => {
    setProvisionUserId("");
    setProvisionDate(null);
    setProvisionItems([{ ppeId: "", quantity: "1" }]);
  };

  const resetPurchaseForm = () => {
    setPurchaseDate(null);
    setPurchaseNotes("");
    setPurchaseItems([{ ppeId: "", quantity: "1" }]);
  };

  const openProvideModal = () => {
    resetProvisionForm();
    setIsProvisionModalOpen(true);
  };

  const openPurchaseModal = () => {
    resetPurchaseForm();
    setIsPurchaseModalOpen(true);
  };

  const downloadProvisionsExcel = async () => {
    setDownloadingProvisionsExcel(true);
    try {
      const total = Math.max(provisionsQuery.data?.meta.total ?? 0, 1);
      const response = useUserProvisionsEndpoint
        ? await getPpeUserProvisions(
            provisionsUserId!,
            1,
            total,
            searchTerm,
          )
        : await getPpeProvisions(
            1,
            total,
            searchTerm,
            provisionsUserId,
            provisionsPpeId,
          );

      const rows = response.data.map((item) => ({
        "Date Issued": item.providedDate
          ? format(new Date(item.providedDate), "yyyy-MM-dd HH:mm")
          : "",
        "PPE/Non Scaffolds User": item.ppeUser?.fullName || "",
        "Job Title": item.ppeUser?.jobTitle || "",
        Location: item.ppeUser?.location || "",
        "PPE/Non Scaffolds": item.ppe
          ? `${item.ppe.name}${item.ppe.size ? ` (${item.ppe.size})` : ""}`
          : "",
        Quantity: Number(item.quantity),
        Unit: item.ppe?.unit || "",
        "Provided By": item.providedByUser?.fullName || "",
      }));

      if (rows.length === 0) {
        toast.error("No provisions to download");
        return;
      }

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Provisions");
      const fileName = `PPE_Provisions_${format(new Date(), "yyyy-MM-dd")}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success("Provisions Excel downloaded");
    } catch (error) {
      toast.error(
        getBackendErrorMessage(error, "Failed to download provisions Excel"),
      );
    } finally {
      setDownloadingProvisionsExcel(false);
    }
  };

  // Warehouses (for filters + create PPE/Non Scaffolds)
  const warehousesQuery = useQuery({
    queryKey: ["warehouses", { page: 1, limit: 1000, search: "" }],
    queryFn: async () => getWarehouses(1, 1000, ""),
  });

  const warehouses: Warehouse[] = warehousesQuery.data?.data ?? [];

  const visibleWarehouses = useMemo(() => {
    if (!user || user.role === "ADMIN") return warehouses;
    const norm = (v: string | null | undefined) => (v ?? "").trim().toLowerCase();
    const uCountry = norm(user.country);
    const uRegion = norm(user.region);
    const uDistrict = norm(user.district);
    const uWard = norm(user.ward);
    const uStreet = norm(user.street);
    if (!uCountry && !uRegion && !uDistrict && !uWard && !uStreet) {
      return warehouses;
    }
    return warehouses.filter((w) => {
      if (uCountry && norm(w.country) !== uCountry) return false;
      if (uRegion && norm(w.region) !== uRegion) return false;
      if (uDistrict && norm(w.district) !== uDistrict) return false;
      if (uWard && norm(w.ward) !== uWard) return false;
      if (uStreet && norm(w.street) !== uStreet) return false;
      return true;
    });
  }, [warehouses, user]);

  const defaultWarehouseId = useMemo(() => {
    if (visibleWarehouses.length === 0) return "";
    let best = visibleWarehouses[0];
    let bestWeight = best.totalWeight ?? 0;
    for (const w of visibleWarehouses) {
      const tw = w.totalWeight ?? 0;
      if (tw > bestWeight) {
        bestWeight = tw;
        best = w;
      }
    }
    return best.id;
  }, [visibleWarehouses]);

  const warehouseFilterOptions = useMemo(() => {
    const opts = visibleWarehouses.map((warehouse) => ({
      label: warehouse.name,
      value: warehouse.id,
    }));
    return [{ label: "All", value: "All" }, ...opts];
  }, [visibleWarehouses]);

  const ppeDataQuery = useMemo((): PpeDataQuery => {
    if (warehousesQuery.isLoading) return { status: "pending" };
    if (visibleWarehouses.length === 0) return { status: "empty" };
    if (selectedWarehouse) return { status: "scoped", warehouseId: selectedWarehouse };
    if (viewsAllWarehouses) return { status: "all" };
    return { status: "scoped", warehouseId: defaultWarehouseId };
  }, [
    warehousesQuery.isLoading,
    visibleWarehouses.length,
    selectedWarehouse,
    viewsAllWarehouses,
    defaultWarehouseId,
  ]);

  const warehouseSelectDisplayValue =
    viewsAllWarehouses
      ? "All"
      : selectedWarehouse || defaultWarehouseId;

  const handleWarehouseFilterChange = (value: string) => {
    if (value === "All") {
      setSelectedWarehouse("");
      setViewsAllWarehouses(true);
    } else {
      setSelectedWarehouse(value);
      setViewsAllWarehouses(false);
    }
  };

  // Default to highest-weight warehouse when nothing is selected / selection invalid
  useEffect(() => {
    if (visibleWarehouses.length === 0 || !defaultWarehouseId) return;
    if (viewsAllWarehouses) return;

    const inVisible = (id: string) => visibleWarehouses.some((w) => w.id === id);
    if (selectedWarehouse && inVisible(selectedWarehouse)) return;

    setSelectedWarehouse(defaultWarehouseId);
    setViewsAllWarehouses(false);
  }, [
    defaultWarehouseId,
    visibleWarehouses,
    selectedWarehouse,
    viewsAllWarehouses,
  ]);

  // Reset stock pagination when the effective warehouse filter changes
  useEffect(() => {
    if (ppeDataQuery.status === "pending") return;
    setStockPage(1);
  }, [ppeDataQuery]);

  // Stock list
  const stockQuery = useQuery({
    queryKey: [
      "ppe",
      "stock",
      {
        page: stockPage,
        limit: stockPageSize,
        search: searchTerm,
        warehouseId: warehouseIdForPpeApi(ppeDataQuery) ?? "__all__",
      },
    ],
    queryFn: async () =>
      getPpeList(
        stockPage,
        stockPageSize,
        searchTerm,
        warehouseIdForPpeApi(ppeDataQuery),
      ),
    enabled:
      tab === "Stock" &&
      (ppeDataQuery.status === "scoped" || ppeDataQuery.status === "all"),
    placeholderData: keepPreviousData,
  });

  // Users list
  const usersQuery = useQuery({
    queryKey: ["ppe", "users", { page: usersPage, limit: usersPageSize, search: searchTerm }],
    queryFn: async () => getPpeUsers(usersPage, usersPageSize, searchTerm),
    enabled: tab === "Users",
    placeholderData: keepPreviousData,
  });

  // Provisions list (user-specific endpoint when a single user is selected)
  const provisionsQuery = useQuery({
    queryKey: useUserProvisionsEndpoint
      ? [
        "ppe",
        "users",
        provisionsUserId,
        "provisions",
        { page: provisionsPage, limit: provisionsPageSize, search: searchTerm },
      ]
      : [
        "ppe",
        "provisions",
        {
          page: provisionsPage,
          limit: provisionsPageSize,
          search: searchTerm,
          ppeUserId: provisionsUserId,
          ppeId: provisionsPpeId,
        },
      ],
    queryFn: async () =>
      useUserProvisionsEndpoint
        ? getPpeUserProvisions(
          provisionsUserId!,
          provisionsPage,
          provisionsPageSize,
          searchTerm,
        )
        : getPpeProvisions(
          provisionsPage,
          provisionsPageSize,
          searchTerm,
          provisionsUserId,
          provisionsPpeId,
        ),
    enabled: tab === "Provisions",
    placeholderData: keepPreviousData,
  });

  const purchasesQuery = useQuery({
    queryKey: [
      "ppe",
      "purchases",
      {
        page: purchasesPage,
        limit: purchasesPageSize,
        search: searchTerm,
        ppeId: purchasesPpeId,
      },
    ],
    queryFn: async () =>
      getPpePurchases(purchasesPage, purchasesPageSize, searchTerm, purchasesPpeId),
    enabled: tab === "Purchases",
    placeholderData: keepPreviousData,
  });

  const statsDateFrom = statsDateRange?.from
    ? format(statsDateRange.from, "yyyy-MM-dd")
    : undefined;
  const statsDateTo = statsDateRange?.to
    ? format(statsDateRange.to, "yyyy-MM-dd")
    : undefined;

  const statisticsQuery = useQuery<PpeStatistics>({
    queryKey: [
      "ppe",
      "statistics",
      {
        warehouseId: warehouseIdForPpeApi(ppeDataQuery) ?? "__all__",
        dateFrom: statsDateFrom,
        dateTo: statsDateTo,
      },
    ],
    queryFn: async () =>
      getPpeStatistics(
        warehouseIdForPpeApi(ppeDataQuery),
        statsDateFrom,
        statsDateTo,
      ),
    enabled: ppeDataQuery.status === "scoped" || ppeDataQuery.status === "all",
  });

  // Lookups for provision modal (all PPE/Non Scaffolds + all users)
  const provisionLookupsQuery = useQuery<{ ppe: Ppe[]; users: PpeUser[] }>({
    queryKey: ["ppe", "provision-lookups"],
    queryFn: async () => {
      const [ppeRes, usersRes] = await Promise.all([getPpeList(1, 1000, ""), getPpeUsers(1, 1000, "")]);
      return { ppe: ppeRes.data, users: usersRes.data };
    },
  });

  useEffect(() => {
    const handler = setTimeout(() => setSearchTerm(searchInput), 350);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    if (!statisticsQuery.error) return;
    toast.error(getBackendErrorMessage(statisticsQuery.error, "Failed to fetch statistics"));
  }, [statisticsQuery.error]);

  useEffect(() => {
    if (tab === "Stock") setStockPage(1);
    if (tab === "Users") setUsersPage(1);
    if (tab === "Provisions") setProvisionsPage(1);
    if (tab === "Purchases") setPurchasesPage(1);
  }, [
    searchTerm,
    tab,
    selectedProvisionPpeId,
    selectedProvisionPpeUserId,
    selectedPurchasePpeId,
  ]);

  const stockActions = [
    {
      label: "Edit PPE/Non Scaffolds",
      icon: Edit,
      onClick: (row: TableRowData) => {
        const raw = row.raw_data as Ppe;
        setEditingPpeId(raw.id);
        setPpeName(raw.name);
        setPpeSize(raw.size || "");
        setPpeCondition(raw.condition);
        setPpeQuantity(String(raw.quantity));
        setPpeUnit(raw.unit || "");
        setPpeWarehouseId(raw.warehouseId);
        setIsPpeModalOpen(true);
      },
    },
  ];

  const provisionActions = [
    {
      label: "View details",
      icon: Icons.Preview,
      onClick: (row: TableRowData) => {
        setSelectedProvisionDetails(row.raw_data as PpeProvision);
      },
    },
  ];

  const purchaseActions = [
    {
      label: "View details",
      icon: Icons.Preview,
      onClick: (row: TableRowData) => {
        setSelectedPurchaseDetails(row.raw_data as PpePurchase);
      },
    },
  ];

  const userActions = [
    {
      label: "View details",
      icon: Icons.Preview,
      onClick: (row: TableRowData) => {
        setSelectedPpeUserDetails(row.raw_data as PpeUser);
      },
    },
    {
      label: "Edit PPE/Non Scaffolds User",
      icon: Edit,
      onClick: (row: TableRowData) => {
        const raw = row.raw_data as PpeUser;
        setEditingPpeUserId(raw.id);
        setPpeUserFullName(raw.fullName);
        setPpeUserJobTitle(raw.jobTitle);
        setPpeUserLocation(raw.location || "");
        setIsPpeUserModalOpen(true);
      },
    },
  ];

  const handleSavePpe = async () => {
    const quantity = parseDecimalQuantity(ppeQuantity);
    if (!ppeName.trim()) return toast.error("PPE/Non Scaffolds name is required");
    if (quantity === null || quantity < 0) {
      return toast.error("Quantity must be a valid number and at least 0");
    }
    if (!ppeUnit.trim()) return toast.error("Unit is required");
    if (!ppeWarehouseId) return toast.error("Warehouse is required");

    setSavingPpe(true);
    try {
      if (editingPpeId) {
        await updatePpe(editingPpeId, {
          name: ppeName.trim(),
          size: ppeSize.trim() || null,
          unit: ppeUnit.trim(),
          condition: ppeCondition,
          quantity,
          warehouseId: ppeWarehouseId,
        });
        toast.success("PPE/Non Scaffolds updated");
      } else {
        await createPpe({
          name: ppeName.trim(),
          size: ppeSize.trim() || null,
          unit: ppeUnit.trim(),
          condition: ppeCondition,
          quantity,
          warehouseId: ppeWarehouseId,
        });
        toast.success("PPE/Non Scaffolds created");
      }
      setIsPpeModalOpen(false);
      resetPpeForm();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["ppe", "stock"] }),
        queryClient.invalidateQueries({ queryKey: ["ppe", "provision-lookups"] }),
        queryClient.invalidateQueries({ queryKey: ["ppe", "provisions"] }),
        queryClient.invalidateQueries({ queryKey: ["ppe", "statistics"] }),
      ]);
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to save PPE/Non Scaffolds"));
    } finally {
      setSavingPpe(false);
    }
  };

  const handleSavePpeUser = async () => {
    if (!ppeUserFullName.trim()) return toast.error("Full name is required");
    if (!ppeUserJobTitle.trim()) return toast.error("Job title is required");

    setSavingPpeUser(true);
    try {
      const location = ppeUserLocation.trim() || null;
      if (editingPpeUserId) {
        await updatePpeUser(editingPpeUserId, {
          fullName: ppeUserFullName.trim(),
          jobTitle: ppeUserJobTitle.trim(),
          location,
        });
        toast.success("PPE/Non Scaffolds user updated");
      } else {
        await createPpeUser({
          fullName: ppeUserFullName.trim(),
          jobTitle: ppeUserJobTitle.trim(),
          location,
        });
        toast.success("PPE/Non Scaffolds user created");
      }
      setIsPpeUserModalOpen(false);
      resetPpeUserForm();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["ppe", "users"] }),
        queryClient.invalidateQueries({ queryKey: ["ppe", "provision-lookups"] }),
        queryClient.invalidateQueries({ queryKey: ["ppe", "provisions"] }),
        queryClient.invalidateQueries({ queryKey: ["ppe", "statistics"] }),
      ]);
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to save PPE/Non Scaffolds user"));
    } finally {
      setSavingPpeUser(false);
    }
  };

  const handleProvision = async () => {
    if (!provisionUserId) return toast.error("Please select PPE/Non Scaffolds user");
    const preparedItems = provisionItems
      .filter((line) => line.ppeId.trim() && line.quantity.trim())
      .map((line) => ({
        ppeId: line.ppeId,
        quantity: parseDecimalQuantity(line.quantity),
      }));

    if (preparedItems.length === 0) {
      return toast.error("Add at least one PPE/Non Scaffolds item");
    }
    const hasInvalidQty = preparedItems.some(
      (line) => line.quantity === null || line.quantity <= 0,
    );
    if (hasInvalidQty) return toast.error("Each quantity must be a valid number greater than 0");

    setSavingProvision(true);
    try {
      await providePpe({
        ppeUserId: provisionUserId,
        providedDate: provisionDate ? provisionDate.toISOString() : undefined,
        items: preparedItems.map((line) => ({
          ppeId: line.ppeId,
          quantity: line.quantity as number,
        })),
      });
      toast.success("PPE/Non Scaffolds provisioned successfully");
      setIsProvisionModalOpen(false);
      resetProvisionForm();
      // Refresh stock after provisioning because quantity is decremented.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["ppe", "stock"] }),
        queryClient.invalidateQueries({ queryKey: ["ppe", "provision-lookups"] }),
        queryClient.invalidateQueries({ queryKey: ["ppe", "provisions"] }),
        queryClient.invalidateQueries({ queryKey: ["ppe", "statistics"] }),
      ]);
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to provide PPE/Non Scaffolds"));
    } finally {
      setSavingProvision(false);
    }
  };

  const handlePurchase = async () => {
    const preparedItems = purchaseItems
      .filter((line) => line.ppeId.trim() && line.quantity.trim())
      .map((line) => ({
        ppeId: line.ppeId,
        quantity: parseDecimalQuantity(line.quantity),
      }));

    if (preparedItems.length === 0) {
      return toast.error("Add at least one PPE/Non Scaffolds item");
    }
    const hasInvalidQty = preparedItems.some(
      (line) => line.quantity === null || line.quantity <= 0,
    );
    if (hasInvalidQty) return toast.error("Each quantity must be a valid number greater than 0");

    setSavingPurchase(true);
    try {
      await purchasePpe({
        purchasedDate: purchaseDate ? purchaseDate.toISOString() : undefined,
        notes: purchaseNotes.trim() || undefined,
        items: preparedItems.map((line) => ({
          ppeId: line.ppeId,
          quantity: line.quantity as number,
        })),
      });
      toast.success("PPE/Non Scaffolds purchase recorded");
      setIsPurchaseModalOpen(false);
      resetPurchaseForm();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["ppe", "stock"] }),
        queryClient.invalidateQueries({ queryKey: ["ppe", "provision-lookups"] }),
        queryClient.invalidateQueries({ queryKey: ["ppe", "purchases"] }),
        queryClient.invalidateQueries({ queryKey: ["ppe", "statistics"] }),
      ]);
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to record PPE/Non Scaffolds purchase"));
    } finally {
      setSavingPurchase(false);
    }
  };

  const allPpeForProvision = provisionLookupsQuery.data?.ppe ?? [];
  const allPpeUsersForProvision = provisionLookupsQuery.data?.users ?? [];
  const statistics = statisticsQuery.data ?? null;

  const provisionUserSelectOptions = useMemo(
    () =>
      allPpeUsersForProvision.map((user) => ({
        value: user.id,
        label: formatPpeUserLabel(user),
      })),
    [allPpeUsersForProvision],
  );

  const provisionPpeSelectOptions = useMemo(
    () =>
      allPpeForProvision.map((ppe) => ({
        value: ppe.id,
        label: `${ppe.name}${ppe.size ? ` (${ppe.size})` : ""} - ${ppe.condition} [Stock: ${formatQuantity(Number(ppe.quantity))}${ppe.unit ? ` ${ppe.unit}` : ""}]`,
      })),
    [allPpeForProvision],
  );

  const monthlyProvisionData = useMemo(
    () =>
      (statistics?.monthlyProvisionTrend ?? []).slice(-6).map((row) => {
        let monthLabel = row.month;
        try {
          monthLabel = format(new Date(`${row.month}-01`), "MMM yyyy");
        } catch {
          // keep raw month if parsing fails
        }
        return {
          month: monthLabel,
          provisions: row.count,
        };
      }),
    [statistics],
  );

  const initialLoading =
    warehousesQuery.isLoading ||
    provisionLookupsQuery.isLoading ||
    statisticsQuery.isLoading;

  const stockTableLoading =
    tab === "Stock" && stockQuery.isFetching && !stockQuery.isLoading;
  const usersTableLoading =
    tab === "Users" && usersQuery.isFetching && !usersQuery.isLoading;
  const provisionsTableLoading =
    tab === "Provisions" && provisionsQuery.isFetching && !provisionsQuery.isLoading;
  const purchasesTableLoading =
    tab === "Purchases" && purchasesQuery.isFetching && !purchasesQuery.isLoading;

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["ppe"] }),
      queryClient.invalidateQueries({ queryKey: ["warehouses"] }),
    ]);

  const openAddPpeModal = () => {
    resetPpeForm();
    setIsPpeModalOpen(true);
  };

  const openAddPpeUserModal = () => {
    resetPpeUserForm();
    setIsPpeUserModalOpen(true);
  };

  const downloadExcelDisabled =
    downloadingProvisionsExcel || (provisionsQuery.data?.meta.total ?? 0) === 0;

  const provideAction = {
    label: "Provide PPE/Non Scaffolds",
    icon: Icons.Movement,
    onClick: openProvideModal,
  };
  const purchaseAction = {
    label: "Record Purchase",
    icon: Icons.InventoryPurchase,
    onClick: openPurchaseModal,
  };

  const mobilePrimaryAction: MobilePrimaryAction =
    tab === "Provisions"
      ? { ...provideAction, icon: Icons.Plus }
      : tab === "Purchases"
        ? { ...purchaseAction, icon: Icons.Plus }
        : tab === "Stock"
          ? { label: "Add PPE/Non Scaffolds", icon: Icons.Plus, onClick: openAddPpeModal }
          : { label: "Add PPE/Non Scaffolds User", icon: Icons.Plus, onClick: openAddPpeUserModal };

  const mobileActions: ActionSheetItem[] =
    tab === "Provisions"
      ? [
        {
          label: downloadingProvisionsExcel ? "Downloading..." : "Download Excel",
          icon: Icons.Download,
          onClick: () => void downloadProvisionsExcel(),
          disabled: downloadExcelDisabled,
        },
      ]
      : tab === "Purchases"
        ? []
        : [provideAction, purchaseAction];

  const warehouseFilterActive =
    viewsAllWarehouses || warehouseSelectDisplayValue !== defaultWarehouseId;

  const activeFilterCount =
    (warehouseFilterActive ? 1 : 0) +
    (statsDateRange?.from ? 1 : 0) +
    (tab === "Provisions"
      ? (selectedProvisionPpeUserId !== "all" ? 1 : 0) +
        (selectedProvisionPpeId !== "all" ? 1 : 0)
      : 0) +
    (tab === "Purchases" && selectedPurchasePpeId !== "all" ? 1 : 0);

  const resetFilters = () => {
    setSelectedWarehouse(defaultWarehouseId);
    setViewsAllWarehouses(false);
    setStatsDateRange(undefined);
    setSelectedProvisionPpeUserId("all");
    setSelectedProvisionPpeId("all");
    setSelectedPurchasePpeId("all");
  };

  const activeWarehouseName = viewsAllWarehouses
    ? "All warehouses"
    : visibleWarehouses.find((w) => w.id === warehouseSelectDisplayValue)?.name;

  const warehouseSelect = (
    <SelectInput
      value={warehouseSelectDisplayValue}
      options={warehouseFilterOptions}
      placeholder="Warehouse"
      onChange={handleWarehouseFilterChange}
      className="min-w-[200px]"
    />
  );

  const provisionUserFilter = (
    <SearchableSelectInput
      value={selectedProvisionPpeUserId === "all" ? "" : selectedProvisionPpeUserId}
      options={provisionUserSelectOptions}
      defaultLabel="All PPE/Non Scaffolds Users"
      onSelect={(value) => setSelectedProvisionPpeUserId(value || "all")}
      className={isMobile ? "w-full" : "min-w-[260px] max-w-sm"}
    />
  );

  const provisionPpeFilter = (
    <SearchableSelectInput
      value={selectedProvisionPpeId === "all" ? "" : selectedProvisionPpeId}
      options={provisionPpeSelectOptions}
      defaultLabel="All PPE/Non Scaffolds Items"
      onSelect={(value) => setSelectedProvisionPpeId(value || "all")}
      className={isMobile ? "w-full" : "min-w-[260px] max-w-sm"}
    />
  );

  const purchasePpeFilter = (
    <SearchableSelectInput
      value={selectedPurchasePpeId === "all" ? "" : selectedPurchasePpeId}
      options={provisionPpeSelectOptions}
      defaultLabel="All PPE/Non Scaffolds Items"
      onSelect={(value) => setSelectedPurchasePpeId(value || "all")}
      className={isMobile ? "w-full" : "min-w-[260px] max-w-sm"}
    />
  );

  const stats: StatItem[] = [
    {
      label: "Stock Records",
      value: statistics?.totalStockRecords ?? 0,
      icon: <Shield className="h-5 w-5 text-primary-600" />,
    },
    {
      label: "Total Stock Quantity",
      value: formatStatQuantity(statistics?.totals.stockQuantity ?? 0),
      icon: <Icons.Inventory className="h-5 w-5 text-primary-600" />,
    },
    {
      label: "Recorded Users",
      value: statistics?.totalPpeUsers ?? 0,
      icon: <UserPlus className="h-5 w-5 text-primary-600" />,
    },
    {
      label: "Provisions",
      value: statistics?.totalProvisions ?? 0,
      icon: <Icons.Movement className="h-5 w-5 text-primary-600" />,
    },
    {
      label: "Purchases",
      value: statistics?.totalPurchases ?? 0,
      icon: <Icons.InventoryPurchase className="h-5 w-5 text-primary-600" />,
    },
  ];

  const charts = (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
      <PpeTopProvisionedChart items={statistics?.topProvisionedItems ?? []} />
      <div className="bg-white p-4 rounded-lg border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-medium text-gray-900">
            Monthly Provisions
          </span>
        </div>
        <div className="h-[240px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={monthlyProvisionData}
              margin={{ top: 8, right: 16, bottom: 0, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 12, fill: "#64748B" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 12, fill: "#64748B" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  fontSize: 12,
                }}
              />
              <Line
                type="monotone"
                dataKey="provisions"
                name="Provisions"
                stroke="#38bdf8"
                strokeWidth={2}
                dot={{ fill: "#38bdf8", r: 4 }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <PageShell
        title="PPE/Non Scaffolds"
        description="Manage PPE/Non Scaffolds stock, users, purchases, and issued provision history."
        loading={initialLoading}
        toolbar={
          <>
            {warehouseSelect}
            <DateRangePicker
              value={statsDateRange}
              onChange={setStatsDateRange}
              className="h-9"
            />
            <RefreshToolbarButton
              isRefreshing={
                warehousesQuery.isFetching ||
                stockQuery.isFetching ||
                usersQuery.isFetching ||
                provisionsQuery.isFetching ||
                purchasesQuery.isFetching ||
                provisionLookupsQuery.isFetching ||
                statisticsQuery.isFetching
              }
              onRefresh={refresh}
            />
          </>
        }
        stats={isMobile ? stats : undefined}
        insights={
          isMobile ? (
            charts
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mt-4">
                {stats.map((item) => (
                  <StatisticsCard
                    key={item.label}
                    label={item.label}
                    value={item.value}
                    icon={item.icon}
                  />
                ))}
              </div>
              {charts}
            </>
          )
        }
        mobile={{
          onRefresh: refresh,
          primaryAction: mobilePrimaryAction,
          actions: mobileActions,
          filters: (
            <>
              <FilterField label="Warehouse">{warehouseSelect}</FilterField>
              <FilterField label="Date range">
                <DateRangePicker
                  value={statsDateRange}
                  onChange={setStatsDateRange}
                  className="h-11 w-full justify-start"
                />
              </FilterField>
              {tab === "Provisions" && (
                <>
                  <FilterField label="PPE/Non Scaffolds User">{provisionUserFilter}</FilterField>
                  <FilterField label="PPE/Non Scaffolds Item">{provisionPpeFilter}</FilterField>
                </>
              )}
              {tab === "Purchases" && (
                <FilterField label="PPE/Non Scaffolds Item">{purchasePpeFilter}</FilterField>
              )}
            </>
          ),
          activeFilterCount,
          onResetFilters: resetFilters,
          headerContent: activeWarehouseName && (
            <span className="inline-flex max-w-full items-center truncate rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
              {activeWarehouseName}
            </span>
          ),
        }}
      >
        <div className={isMobile ? "space-y-4" : undefined}>
          <div className={isMobile ? undefined : "flex items-center justify-between gap-3 flex-wrap mt-5"}>
            <TabBar className="flex-nowrap">
              <TabButton
                label="Stock"
                count={statistics?.totalStockRecords ?? stockQuery.data?.meta.total ?? 0}
                active={tab === "Stock"}
                onClick={() => {
                  setTab("Stock");
                }}
              />
              <TabButton
                label="Users"
                count={statistics?.totalPpeUsers ?? usersQuery.data?.meta.total ?? 0}
                active={tab === "Users"}
                onClick={() => {
                  setTab("Users");
                }}
              />
              <TabButton
                label="Provisions"
                count={statistics?.totalProvisions ?? provisionsQuery.data?.meta.total ?? 0}
                active={tab === "Provisions"}
                onClick={() => {
                  setTab("Provisions");
                }}
              />
              <TabButton
                label="Purchases"
                count={statistics?.totalPurchases ?? purchasesQuery.data?.meta.total ?? 0}
                active={tab === "Purchases"}
                onClick={() => {
                  setTab("Purchases");
                }}
              />
            </TabBar>
            <DesktopOnly>
              <div className="flex items-center gap-2 flex-wrap ml-auto">
                {tab === "Provisions" ? (
                  <>
                    <Button
                      variant="outline"
                      className="h-9"
                      onClick={() => void downloadProvisionsExcel()}
                      disabled={downloadExcelDisabled}
                    >
                      <Icons.Download className="h-4 w-4 mr-2" />
                      {downloadingProvisionsExcel
                        ? "Downloading..."
                        : "Download Excel"}
                    </Button>
                    <Button className="h-9" onClick={openProvideModal}>
                      <Icons.Plus className="h-4 w-4 mr-2" />
                      Provide PPE/Non Scaffolds
                    </Button>
                  </>
                ) : tab === "Purchases" ? (
                  <Button className="h-9" onClick={openPurchaseModal}>
                    <Icons.Plus className="h-4 w-4 mr-2" />
                    Record Purchase
                  </Button>
                ) : (
                  <>
                    <Button variant="outline" className="h-9" onClick={openProvideModal}>
                      <Icons.Movement className="h-4 w-4 mr-2" />
                      Provide PPE/Non Scaffolds
                    </Button>
                    <Button variant="outline" className="h-9" onClick={openPurchaseModal}>
                      <Icons.InventoryPurchase className="h-4 w-4 mr-2" />
                      Record Purchase
                    </Button>
                    {tab === "Stock" ? (
                      <Button className="h-9" onClick={openAddPpeModal}>
                        <Icons.Plus className="h-4 w-4 mr-2" />
                        Add PPE/Non Scaffolds
                      </Button>
                    ) : (
                      <Button className="h-9" onClick={openAddPpeUserModal}>
                        <Icons.Plus className="h-4 w-4 mr-2" />
                        Add PPE/Non Scaffolds User
                      </Button>
                    )}
                  </>
                )}
              </div>
            </DesktopOnly>
          </div>

          <div className={isMobile ? undefined : "flex items-center gap-3 flex-wrap mt-5"}>
            <SearchInput
              searchTerm={searchInput}
              onSearchChange={setSearchInput}
              placeholder={
                tab === "Stock"
                  ? "Search PPE/Non Scaffolds..."
                  : tab === "Users"
                    ? "Search users, job titles, locations..."
                    : tab === "Purchases"
                      ? "Search purchases, items, notes, warehouses..."
                      : "Search provisions, users, locations..."
              }
            />
            <DesktopOnly>
              {tab === "Provisions" && (
                <>
                  {provisionUserFilter}
                  {provisionPpeFilter}
                </>
              )}
              {tab === "Purchases" && purchasePpeFilter}
            </DesktopOnly>
          </div>

          <div className={isMobile ? undefined : "mt-4"}>
            {tab === "Stock" ? (
              <DataTable
                columns={ppeStockTableColumns}
                data={
                  (stockQuery.data?.data ?? []).map((item) => ({
                    id: item.id,
                    name: item.name,
                    size: item.size || "—",
                    condition: item.condition,
                    quantity: formatQuantity(Number(item.quantity)),
                    unit: item.unit || "—",
                    warehouse_name: item.warehouse?.name || "—",
                    updated_at: format(new Date(item.updatedAt), "MMM dd, yyyy"),
                    raw_data: item,
                  })) as TableRowData[]
                }
                actions={stockActions}
                loading={stockQuery.isLoading || stockTableLoading}
                pagination={{
                  page: stockPage,
                  pageSize: stockPageSize,
                  totalItems: stockQuery.data?.meta.total ?? 0,
                  totalPages: stockQuery.data?.meta.totalPages ?? 1,
                  onPageChange: (page) => {
                    setStockPage(page);
                  },
                  onPageSizeChange: (size) => {
                    setStockPageSize(size);
                  },
                }}
              />
            ) : tab === "Users" ? (
              <DataTable
                columns={ppeUsersTableColumns}
                data={
                  (usersQuery.data?.data ?? []).map((item) => ({
                    id: item.id,
                    full_name: item.fullName,
                    job_title: item.jobTitle,
                    location: item.location || "—",
                    updated_at: format(new Date(item.updatedAt), "MMM dd, yyyy"),
                    raw_data: item,
                  })) as TableRowData[]
                }
                actions={userActions}
                loading={usersQuery.isLoading || usersTableLoading}
                pagination={{
                  page: usersPage,
                  pageSize: usersPageSize,
                  totalItems: usersQuery.data?.meta.total ?? 0,
                  totalPages: usersQuery.data?.meta.totalPages ?? 1,
                  onPageChange: (page) => {
                    setUsersPage(page);
                  },
                  onPageSizeChange: (size) => {
                    setUsersPageSize(size);
                  },
                }}
              />
            ) : tab === "Provisions" ? (
              <DataTable
                columns={ppeProvisionsTableColumns}
                actions={provisionActions}
                data={
                  (provisionsQuery.data?.data ?? []).map((item: PpeProvision) => ({
                    id: item.id,
                    provided_date: item.providedDate
                      ? format(new Date(item.providedDate), "MMM dd, yyyy HH:mm")
                      : "—",
                    ppe_user: item.ppeUser?.fullName || "—",
                    job_title: item.ppeUser?.jobTitle || "—",
                    location: item.ppeUser?.location || "—",
                    ppe_name: item.ppe
                      ? `${item.ppe.name}${item.ppe.size ? ` (${item.ppe.size})` : ""}`
                      : "—",
                    quantity: formatQuantity(Number(item.quantity)),
                    unit: item.ppe?.unit || "—",
                    provided_by: item.providedByUser?.fullName || "—",
                    documents_count: item.documents?.length ?? 0,
                    raw_data: item,
                  })) as TableRowData[]
                }
                loading={provisionsQuery.isLoading || provisionsTableLoading}
                pagination={{
                  page: provisionsPage,
                  pageSize: provisionsPageSize,
                  totalItems: provisionsQuery.data?.meta.total ?? 0,
                  totalPages: provisionsQuery.data?.meta.totalPages ?? 1,
                  onPageChange: (page) => {
                    setProvisionsPage(page);
                  },
                  onPageSizeChange: (size) => {
                    setProvisionsPageSize(size);
                  },
                }}
              />
            ) : (
              <DataTable
                columns={ppePurchasesTableColumns}
                actions={purchaseActions}
                data={
                  (purchasesQuery.data?.data ?? []).map((item: PpePurchase) => ({
                    id: item.id,
                    purchased_date: item.purchasedDate
                      ? format(new Date(item.purchasedDate), "MMM dd, yyyy HH:mm")
                      : "—",
                    ppe_name: item.ppe
                      ? `${item.ppe.name}${item.ppe.size ? ` (${item.ppe.size})` : ""}`
                      : "—",
                    quantity: formatQuantity(Number(item.quantity)),
                    unit: item.ppe?.unit || "—",
                    warehouse_name: item.ppe?.warehouse?.name || "—",
                    notes: item.notes?.trim() || "—",
                    purchased_by: item.purchasedByUser?.fullName || "—",
                    documents_count: item.documents?.length ?? 0,
                    raw_data: item,
                  })) as TableRowData[]
                }
                loading={purchasesQuery.isLoading || purchasesTableLoading}
                pagination={{
                  page: purchasesPage,
                  pageSize: purchasesPageSize,
                  totalItems: purchasesQuery.data?.meta.total ?? 0,
                  totalPages: purchasesQuery.data?.meta.totalPages ?? 1,
                  onPageChange: (page) => {
                    setPurchasesPage(page);
                  },
                  onPageSizeChange: (size) => {
                    setPurchasesPageSize(size);
                  },
                }}
              />
            )}
          </div>
        </div>
      </PageShell>

      <Modal
        isOpen={isPpeModalOpen}
        onClose={() => setIsPpeModalOpen(false)}
        title={editingPpeId ? "Edit PPE/Non Scaffolds" : "Create PPE/Non Scaffolds"}
      >
        <div className="p-6 space-y-4">
          <div className="space-y-1">
            <label className="text-sm text-gray-600">Name</label>
            <Input value={ppeName} onChange={(e) => setPpeName(e.target.value)} />
          </div>
          <div className="space-y-1">
            <label className="text-sm text-gray-600">Size (Optional)</label>
            <Input value={ppeSize} onChange={(e) => setPpeSize(e.target.value)} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="space-y-1">
              <label className="text-sm text-gray-600">Condition</label>
              <SelectInput
                value={ppeCondition}
                options={PPE_CONDITIONS.map((condition) => ({
                  value: condition,
                  label: condition,
                }))}
                placeholder="Condition"
                onChange={(value) => setPpeCondition(value as PpeCondition)}
                className="w-full"
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm text-gray-600">Quantity</label>
              <Input
                type="number"
                min={0}
                step="any"
                value={ppeQuantity}
                onChange={(e) => setPpeQuantity(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm text-gray-600">Unit</label>
              <Input
                value={ppeUnit}
                onChange={(e) => setPpeUnit(e.target.value)}
                placeholder="e.g. Pc., Kg, L"
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-sm text-gray-600">Warehouse</label>
            <SelectInput
              value={ppeWarehouseId || EMPTY_SELECT_VALUE}
              options={[
                { value: EMPTY_SELECT_VALUE, label: "Select warehouse" },
                ...visibleWarehouses.map((w) => ({ value: w.id, label: w.name })),
              ]}
              placeholder="Select warehouse"
              onChange={(value) =>
                setPpeWarehouseId(value === EMPTY_SELECT_VALUE ? "" : value)
              }
              className="w-full"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setIsPpeModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePpe} disabled={savingPpe}>
              {savingPpe ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isPpeUserModalOpen}
        onClose={() => setIsPpeUserModalOpen(false)}
        title={editingPpeUserId ? "Edit PPE/Non Scaffolds User" : "Create PPE/Non Scaffolds User"}
      >
        <div className="p-6 space-y-4">
          <div className="space-y-1">
            <label className="text-sm text-gray-600">Full Name</label>
            <Input
              value={ppeUserFullName}
              onChange={(e) => setPpeUserFullName(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm text-gray-600">Job Title</label>
            <Input
              value={ppeUserJobTitle}
              onChange={(e) => setPpeUserJobTitle(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm text-gray-600">Location (Optional)</label>
            <Input
              value={ppeUserLocation}
              onChange={(e) => setPpeUserLocation(e.target.value)}
              placeholder="e.g. Site A - Dar es Salaam"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setIsPpeUserModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePpeUser} disabled={savingPpeUser}>
              {savingPpeUser ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isProvisionModalOpen}
        onClose={() => setIsProvisionModalOpen(false)}
        title="Provide PPE/Non Scaffolds"
      >
        <div className="p-6 space-y-4">
          <div className="space-y-1">
            <label className="text-sm text-gray-600">PPE/Non Scaffolds User</label>
            <SearchableSelectInput
              value={provisionUserId}
              options={provisionUserSelectOptions}
              defaultLabel="Select user"
              onSelect={setProvisionUserId}
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm text-gray-600">Provided Date (Optional)</label>
            <CalendarDatePicker
              selected={provisionDate}
              onChange={setProvisionDate}
              className="w-full"
            />
          </div>

          <div className="space-y-3">
            {provisionItems.map((line, index) => (
              <div key={`${index}-${line.ppeId}`} className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                <div className="sm:col-span-8">
                  <SearchableSelectInput
                    value={line.ppeId}
                    options={provisionPpeSelectOptions}
                    defaultLabel="Select PPE/Non Scaffolds item"
                    onSelect={(value) => {
                      const next = [...provisionItems];
                      next[index].ppeId = value;
                      setProvisionItems(next);
                    }}
                  />
                </div>
                <div className="sm:col-span-3">
                  <Input
                    type="number"
                    min={0}
                    step="any"
                    placeholder="Qty"
                    value={line.quantity}
                    onChange={(e) => {
                      const next = [...provisionItems];
                      next[index].quantity = e.target.value;
                      setProvisionItems(next);
                    }}
                  />
                </div>
                <div className="sm:col-span-1 flex items-center justify-end">
                  <Button
                    variant="ghost"
                    type="button"
                    className="h-9"
                    disabled={provisionItems.length === 1}
                    onClick={() =>
                      setProvisionItems((prev) => prev.filter((_, idx) => idx !== index))
                    }
                  >
                    -
                  </Button>
                </div>
              </div>
            ))}
            <Button
              variant="outline"
              type="button"
              onClick={() =>
                setProvisionItems((prev) => [...prev, { ppeId: "", quantity: "1" }])
              }
            >
              Add PPE/Non Scaffolds Line
            </Button>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setIsProvisionModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleProvision} disabled={savingProvision}>
              {savingProvision ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={isPurchaseModalOpen}
        onClose={() => setIsPurchaseModalOpen(false)}
        title="Record PPE/Non Scaffolds Purchase"
      >
        <div className="p-6 space-y-4">
          <div className="space-y-1">
            <label className="text-sm text-gray-600">Purchased Date (Optional)</label>
            <CalendarDatePicker
              selected={purchaseDate}
              onChange={setPurchaseDate}
              className="w-full"
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm text-gray-600">Notes (Optional)</label>
            <Textarea
              value={purchaseNotes}
              onChange={(e) => setPurchaseNotes(e.target.value)}
              placeholder="e.g. Restock from supplier ABC"
              rows={3}
            />
          </div>

          <div className="space-y-3">
            {purchaseItems.map((line, index) => (
              <div key={`${index}-${line.ppeId}`} className="grid grid-cols-1 sm:grid-cols-12 gap-2">
                <div className="sm:col-span-8">
                  <SearchableSelectInput
                    value={line.ppeId}
                    options={provisionPpeSelectOptions}
                    defaultLabel="Select PPE/Non Scaffolds item"
                    onSelect={(value) => {
                      const next = [...purchaseItems];
                      next[index].ppeId = value;
                      setPurchaseItems(next);
                    }}
                  />
                </div>
                <div className="sm:col-span-3">
                  <Input
                    type="number"
                    min={0}
                    step="any"
                    placeholder="Qty"
                    value={line.quantity}
                    onChange={(e) => {
                      const next = [...purchaseItems];
                      next[index].quantity = e.target.value;
                      setPurchaseItems(next);
                    }}
                  />
                </div>
                <div className="sm:col-span-1 flex items-center justify-end">
                  <Button
                    variant="ghost"
                    type="button"
                    className="h-9"
                    disabled={purchaseItems.length === 1}
                    onClick={() =>
                      setPurchaseItems((prev) => prev.filter((_, idx) => idx !== index))
                    }
                  >
                    -
                  </Button>
                </div>
              </div>
            ))}
            <Button
              variant="outline"
              type="button"
              onClick={() =>
                setPurchaseItems((prev) => [...prev, { ppeId: "", quantity: "1" }])
              }
            >
              Add PPE/Non Scaffolds Line
            </Button>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setIsPurchaseModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handlePurchase} disabled={savingPurchase}>
              {savingPurchase ? "Submitting..." : "Submit"}
            </Button>
          </div>
        </div>
      </Modal>

      <PpeUserProvisionsDrawer
        selectedUser={selectedPpeUserDetails}
        onClose={() => setSelectedPpeUserDetails(null)}
      />

      <PpeRecordDetailsDrawer
        kind="provisions"
        record={selectedProvisionDetails}
        onClose={() => setSelectedProvisionDetails(null)}
      />

      <PpeRecordDetailsDrawer
        kind="purchases"
        record={selectedPurchaseDetails}
        onClose={() => setSelectedPurchaseDetails(null)}
      />
    </>
  );
};

export default PpePage;
